import {gsap} from 'gsap'

export type AnimationParallaxElement = {
  element: Element
  animationType?: 'default' | 'scale'
}

// Animation functions that can be called with any elements
export const animateParallax = (animationElements: AnimationParallaxElement[], content: Element) => {
  if (typeof window !== 'undefined' && animationElements.length > 0 && content) {
    // Detect if `motion reduced` is enabled
    const isMotionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!isMotionReduced) {
      let ticking = false

      // Cache DOM queries once during initialization
      const cachedElements = animationElements.map(({element, animationType = 'default'}) => ({
        item: element.querySelector('[data-animate="item"]'),
        animationType,
      }))

      const updateParallax = () => {
        const contentRect = content.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        // Calculate scroll progress (0 to 1) based on content position
        const scrollProgress = Math.max(
          0,
          Math.min(1, (viewportHeight - contentRect.top) / (viewportHeight + contentRect.height)),
        )

        for (const {item, animationType} of cachedElements) {
          if (animationType === 'scale') {
            // Parallax scale effect: starts at 1.1 when below viewport, scales to 1.0 at 25% progress
            const scaleProgress = Math.min(1, scrollProgress / 0.25) // Normalize to reach 1.0 at 25% scroll
            const scale = 1.1 - scaleProgress * 0.1 // 1.1 → 1.0
            if (item) gsap.set(item, {scale})
          } else {
            // Parallax slide down effect
            const yOffset = -40 // Total offset in px: -40 → 0
            const yProgress = Math.min(1, scrollProgress / 0.25) // Normalize to reach end at 25% scroll
            const y = yOffset - yProgress * yOffset
            if (item) gsap.set(item, {y})
          }
        }

        ticking = false
      }

      const onScroll = () => {
        if (!ticking) {
          requestAnimationFrame(updateParallax)
          ticking = true
        }
      }

      // Initial state
      updateParallax()

      // Listen to scroll events. This is better suited here than IntersectionObserver for parallax effects.
      // eslint-disable-next-line github/prefer-observers
      window.addEventListener('scroll', onScroll)

      return () => {
        window.removeEventListener('scroll', onScroll)
      }
    }
  }

  return () => {}
}
